import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { ResStatus } from 'api/api.types';
import {
  useRemoveInvoiceLine,
  useRemoveCorrectiveInvoiceLine,
} from 'api/combinedRequests/invoices';
import { StyledButton } from 'components/Buttons';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { FetchInvoiceData } from 'pages/invoices/components/InvoiceDetails/InvoiceDetails.types';
import {
  CorrectiveInvoice,
  Invoice,
  InvoiceLine,
} from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { appToast } from 'utils/toast.utils';
import { StyledDialog } from '../../InvoiceLinesPanel.css';

type Props = {
  open: boolean;
  isCorrective: boolean;
  isPrevious?: boolean;
  invoice: Invoice | CorrectiveInvoice;
  invoiceLine: InvoiceLine | null;
  onClose: () => void;
  refetchInvoiceData: FetchInvoiceData;
};

export const DeleteInvoiceDialog = ({
  open,
  isCorrective,
  isPrevious,
  invoiceLine,
  invoice,
  onClose,
  refetchInvoiceData,
}: Props) => {
  const { t } = useTranslation(['misc']);

  const { removeInvoiceLine, loading: removeLineLoading } =
    useRemoveInvoiceLine();
  const { removeCorrectiveInvoiceLine, loading: removeCorrectiveLineLoading } =
    useRemoveCorrectiveInvoiceLine();

  const handleSubmit = async () => {
    if (invoiceLine) {
      const res = await removeInvoiceLine(invoice, invoiceLine);
      !!res &&
        res.status === ResStatus.OK &&
        appToast.success(t('message.updateSuccess', { ns: 'misc' }));
      onClose();
      await refetchInvoiceData(invoice.id, isCorrective);
    }
  };

  const handleSubmitCorrective = async () => {
    if (invoiceLine) {
      const res = await removeCorrectiveInvoiceLine(
        invoice as CorrectiveInvoice,
        invoiceLine,
        !!isPrevious,
      );
      !!res &&
        res.status === ResStatus.OK &&
        appToast.success(t('message.updateSuccess', { ns: 'misc' }));
      onClose();
      await refetchInvoiceData(invoice.id, isCorrective);
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <LoaderFullScreen
        loading={removeLineLoading || removeCorrectiveLineLoading}
      />
      {invoiceLine && (
        <>
          <DialogTitle>
            {t('modal.deleteEntityHeading', { ns: 'misc' })}
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText>
              {t('modal.deleteEntityBody', { ns: 'misc' })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <StyledButton
              $primary
              onClick={!isCorrective ? handleSubmit : handleSubmitCorrective}
            >
              {t('button.delete', { ns: 'misc' })}
            </StyledButton>
            <StyledButton onClick={onClose}>
              {t('button.cancel', { ns: 'misc' })}
            </StyledButton>
          </DialogActions>
        </>
      )}
    </StyledDialog>
  );
};
